import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import {TablePagination, Select, MenuItem, useTheme} from "@material-ui/core";

import SystemContext from "../../../SystemContext";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import CompanyMessagingValidator from "../validators/CompanyMessagingValidator";
import { useCompanyMessaging, useAddCompanyMessaging, useUpdateCompanyMessaging, useDeleteCompanyMessaging } from "../../../services/companies";
import {
  MaskedText,
} from "../../../controls";
import MessageTypeLookup from "../../Settings/components/MessageTypeLookup";
import {formatEmailInput} from "../../../functions/EmailValidation";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import SftpInterfaceLookup from "../../Settings/components/SftpInterfaceLookup";

const formatValue = (type, value) => {

  if (type === "Email" && value)
    return(formatEmailInput(value));
  return value;
}

const CompanyMessagingList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const validatorColumn = ValidatorColumn(CompanyMessagingValidator, true);

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const [companyRecord, setCompanyRecord] = useState(props.record)

  const eventBus = SystemData.eventBus;
  if (eventBus) {
    eventBus.bind('CompanyManagementList.rowSelected', (record)=> {
      setCompanyRecord(record);
    });
  }

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddCompanyMessaging = useAddCompanyMessaging();
  const handleUpdateCompanyMessaging = useUpdateCompanyMessaging();
  const handleDeleteCompanyMessaging = useDeleteCompanyMessaging();

  const queryClient = useQueryClient();
  const extraKeys = [companyRecord.companyUuid];
  const companyMessaging = useCompanyMessaging({tableRef, queryClient, extraKeys});

  const ediMethods = SystemData.ediMethods;

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={
      "U": "[A-Za-z*]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...ColumnWidth(150),
        title: "Message Code",
        field: "messageCode",
        initialEditValue: "",
        editComponent: (props) => (
          <MessageTypeLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(150),
        title: "Sub Code",
        field: "messageSubCodes",
        initialEditValue: "",
      },
      {
        ...ColumnWidth(15),
        ...baseColumn,
      },
      {
        ...baseColumn,
        title: "At Airports        ",
        field: "atAirports",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"UUU,".repeat(10)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            forceUpperCase={true}
            value={props.value}
            onChange={e => {
              let data = { ...props.rowData };
              let trimmedAirports = e.toString();
              if (trimmedAirports.length > 0 && trimmedAirports.charAt(trimmedAirports.length -1) === ",")
                trimmedAirports = trimmedAirports.substr(0, trimmedAirports.length -1);
              data.atAirports = trimmedAirports;
              props.onRowDataChange(data);
            }}
            //onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(200),
        title: "Delivery Method",
        field: "deliveryMethod",
        initialEditValue: "",
        editComponent: (props) => (
          <Select
            labelId="Delivery Method"
            id="Delivery Method"
            value={props.value}
            onChange={e => {
              props.onChange(e.target.value);
            }}
          >
            {ediMethods.map((item) => {
              return (<MenuItem key={item} value={item}>{item}</MenuItem>);
            })
            }
          </Select>
        ),
        render: (rowData) => (<div>{rowData.deliveryMethod}</div>),
      },
      {
        ...baseColumn,
        ...ColumnWidth(350),
        title: "Delivery Address",
        field: "deliveryAddress",
        initialEditValue: "",
        editComponent: (props) =>
          {
            return (
              props.rowData.deliveryMethod === "SFTP"
                ?
                  <SftpInterfaceLookup
                    id="deliveryAddress"
                    style={{width: "100%"}}
                    returnType="object"
                    interfaceSftpCode={props.rowData.deliveryAddress}
                    value={props.value}
                    onChange={(object)=> {
                      let deliveryAddress = "";
                      let validSftpInterface = false;
                      if (object.found){
                        deliveryAddress = object.data.interfaceSftpCode;
                        validSftpInterface = true;
                      } else {
                        deliveryAddress = object.value;
                      }
                      const data = { ...props.rowData, deliveryAddress: deliveryAddress, validSftpInterface };
                      props.onRowDataChange(data);
                    }}
                  />
                :
                  <MaskedText
                    mask={"x".repeat(100)}
                    maskChar={""}
                    alwaysShowMask={false}
                    formatChars={formatChars}
                    value={props.value}
                    onChange={(value) => {
                      props.onChange(formatValue(props.rowData.deliveryMethod, value));
                    }}
                    inputProps={defaultEditProps}
                  />
            )
          }
      },
    ];
  };

  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: props.fullPage ? 15 : 5,
              detailPanelType: "single",
              //showTitle: false,
              //search: false
              minBodyHeight: props.fullPage ? "calc(100vh - 340px)" : undefined, //FIXME to calculate dynamic height, needed for correct scroll position identification
              maxBodyHeight: props.fullPage ? "calc(100vh - 340px)" : undefined
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title={"Required Messaging" + (companyRecord.companyName === undefined ? " (No Company Selected)" : " for " + companyRecord.companyName)}
            columns={getColumns({canEdit: state.canEdit})}
            data={companyMessaging.data}
            editable={{
              isEditable: () => {
                return companyRecord.companyUuid && state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: companyRecord.companyUuid && state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                    const messageSubCodes = newData.messageSubCodes === "" ? null : newData.messageSubCodes;
                    handleAddCompanyMessaging.mutate({record: {...newData, companyUuid: companyRecord.companyUuid, messageSubCodes}, resolve, reject, extraKeys});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    const messageSubCodes = newData.messageSubCodes === "" ? null : newData.messageSubCodes;
                    handleUpdateCompanyMessaging.mutate({record: {...newData, companyUuid: companyRecord.companyUuid, messageSubCodes}, oldRecord: oldData, resolve, reject, extraKeys});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteCompanyMessaging.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                  }),
            }}
        />
      </div>
  );

}

export default CompanyMessagingList;
